/**
 * Zentraler State fuer die Assistent-Animation.
 *
 * State-Transition (semantisch korrekt — folgt dem Stream-Event-Fluss):
 *   stream-start            → 'searching'   (Lupe raus)
 *   tool_start              → 'searching'   (Tool laeuft, zurueck zu Lupe)
 *   tool_result             → 'creating'    (Daten da, LLM komponiert Antwort)
 *   result_panel-Event      → 'creating'    (Panel wird gerendert)
 *   stream-ende mit Action  → 'success'     (1.5s Triumph, dann 'idle')
 *   stream-ende ohne Action → 'idle'        (z.B. kurzer Chitchat)
 *
 * Wird im ChatPanel gesetzt (eine Wahrheitsquelle) und im
 * AssistantStateOverlay konsumiert. Bei nicht-eingebundenem Provider liefert
 * der Hook 'idle' — Overlay rendert nichts.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

export type AssistantState = 'idle' | 'searching' | 'creating' | 'success';

interface Ctx {
  state: AssistantState;
  /** Direkter State-Override (selten). */
  set(state: AssistantState): void;
  /** Stream startet — searching + Action-Flag reset. */
  beginStream(): void;
  /** Tool laeuft (z.B. list_tasks) — zurueck zu searching. */
  noteToolStart(): void;
  /** Tool fertig — LLM komponiert die Antwort → creating. */
  noteToolEnd(): void;
  /** Result-Panel-Event empfangen — creating + Action-Flag. */
  notePanel(): void;
  /** Stream-Ende: success (wenn Action passierte) → idle, sonst direkt idle. */
  endStream(): void;
}

const FALLBACK: Ctx = {
  state: 'idle',
  set: () => {},
  beginStream: () => {},
  noteToolStart: () => {},
  noteToolEnd: () => {},
  notePanel: () => {},
  endStream: () => {},
};

const AssistantStateCtx = createContext<Ctx>(FALLBACK);

export function AssistantStateProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AssistantState>('idle');
  const hadAction = useRef(false);
  const successTimer = useRef<number | null>(null);

  const set = useCallback((s: AssistantState) => {
    if (successTimer.current !== null) {
      clearTimeout(successTimer.current);
      successTimer.current = null;
    }
    setState(s);
  }, []);

  const beginStream = useCallback(() => {
    hadAction.current = false;
    set('searching');
  }, [set]);

  const noteToolStart = useCallback(() => {
    hadAction.current = true;
    set('searching');
  }, [set]);

  const noteToolEnd = useCallback(() => {
    hadAction.current = true;
    set('creating');
  }, [set]);

  const notePanel = useCallback(() => {
    hadAction.current = true;
    set('creating');
  }, [set]);

  const endStream = useCallback(() => {
    if (hadAction.current) {
      set('success');
      successTimer.current = window.setTimeout(() => {
        successTimer.current = null;
        setState('idle');
      }, 1500);
    } else {
      set('idle');
    }
  }, [set]);

  useEffect(() => () => {
    if (successTimer.current !== null) clearTimeout(successTimer.current);
  }, []);

  const value = useMemo<Ctx>(
    () => ({ state, set, beginStream, noteToolStart, noteToolEnd, notePanel, endStream }),
    [state, set, beginStream, noteToolStart, noteToolEnd, notePanel, endStream],
  );
  return <AssistantStateCtx.Provider value={value}>{children}</AssistantStateCtx.Provider>;
}

export function useAssistantState(): Ctx {
  return useContext(AssistantStateCtx);
}
